import { getCurrentToken } from '../utils/auth';
import example from '../pages/art/utils/example_3d.json';

export type AI3DRequest = {
    image_url: string;
    textured_mesh: boolean;

    seed?: number;
    num_inference_steps?: number;
    guidance_scale?: number;
    octree_resolution?: number;
}

export type AIResult = { 
    file_size: number;
    file_name: string;
    content_type: string;
    url: string;
    file_data?: string;
}

interface generate3DProps {
    imageUrl: string;
    config: any;
    onStart?: () => void;
    onComplete?: (result: AIResult) => void;
    onError?: (error: Error) => void;
}

/**
 * Generate a 3D model from an image
 * 
 * @param req - The request parameters for 3D generation
 * @param config - Application configuration
 * @param onStart - Optional callback when generation starts
 * @param onComplete - Optional callback when generation completes successfully
 * @param onError - Optional callback when generation fails
 * @returns Promise with the 3D model result
 */
export const generateAI3D = async ({ imageUrl, config, onStart, onError, onComplete }: generate3DProps): Promise<AIResult> => {
    return new Promise(async (resolve, reject) => {
        try {
            // Call onStart callback if provided
            if (onStart) {
                onStart();
            }

            if (config.DEBUG_DISABLE_3D_AI) {
                // console.log('3D generation disabled - simulating delay');
                
                // Create a timeout to simulate network delay
                const simulatedDelayMs = 5000; // delay
                const timeoutId = setTimeout(() => {
                    // console.log('Simulated 3D generation complete');
                    const result = example;
                    
                    // Call onComplete callback if provided
                    if (onComplete) {
                        onComplete(result);
                    }
                    
                    resolve(result);
                }, simulatedDelayMs);
                
                return;
            }

            const req: AI3DRequest = {
                image_url: imageUrl,
                textured_mesh: true, // 3x price for true

                seed: 42, // default 42
                num_inference_steps: 50, // default 50
                guidance_scale: 7.5, // default 7.5
                octree_resolution: 256 // default 256
            };

            // console.log('Sending request to Supabase Edge Function:', req);

            // Create a timeout for the request
            const timeoutId = setTimeout(() => {
                console.error('3D generation timed out after', config.GENERATE_3D_TIMEOUT, 'ms');
                const error = new Error('3D generation timed out');
                if (onError) onError(error);
                reject(error);
            }, config.GENERATE_3D_TIMEOUT);

            const authToken = await getCurrentToken();

            // Call the Supabase Edge Function
            fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-3d`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(req)
            })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                clearTimeout(timeoutId);
                // console.log('Received 3D result: ', data);
                const result = { 
                    url: data.url, 
                    file_size: data.file_size, 
                    file_name: data.file_name, 
                    content_type: data.content_type,
                    file_data: data.file_data,
                };
                
                // Call onComplete callback if provided
                if (onComplete) {
                    onComplete(result);
                }
                
                resolve(result);
            })
            .catch(error => {
                clearTimeout(timeoutId);
                console.error('Error during 3D generation:', error);
                
                // Call onError callback if provided
                if (onError) {
                    onError(error);
                }
                
                reject(error);
            });
        } catch (error) {
            console.error('Error during 3D generation setup:', error);
            if (onError) onError(error);
            reject(error);
        }
    });
};

/**
 * Helper function to generate a 3D model with default parameters
 * This function can be used directly in components like ArtEdit.tsx
 * 
 * @param config - Application configuration
 * @param imageUrl - URL of the image to generate 3D model from
 * @param setIsGenerating - State setter for tracking generation status
 * @param onResult - Callback for handling the result
 * @returns Promise with the 3D model result
 */
export const generate3D = async ({
    config,
    imageUrl,
    setIsGenerating,
    onResult
}: {
    config: any;
    imageUrl: string;
    setIsGenerating?: (isGenerating: boolean) => void;
    onResult?: (result: AIResult) => void;
}): Promise<AIResult> => {
    
    // console.log('Generating 3D model', imageUrl);
    
    return generateAI3D({
        imageUrl,
        config,
        onStart: () => setIsGenerating?.(true),
        onComplete: (result) => {
            if (onResult) onResult(result);
            setIsGenerating?.(false);
        },
        onError: (error) => {
            console.error('3D generation failed:', error);
            setIsGenerating?.(false);
        }
    });
};
    